import type { CustomAttributeData } from '@risksmart-app/domain/src/types/common.types';
import { DocumentFileType } from '@risksmart-app/domain/src/types/consts/document-file-type';
import type {
  InputMaybe,
  InsertDocumentVersionMutation,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  Document_File_Type_Enum,
  InsertDocumentVersionDocument,
  namedOperations,
} from '@risksmart-app/web-graphql-client/generated/graphql';

import { createMutationHook } from '@/utils/hook-factories';

interface InsertDocumentVersionSharedInput {
  ParentDocumentId: string;
  Version?: InputMaybe<string>;
  Summary?: InputMaybe<string>;
  ReasonForReview?: InputMaybe<string>;
  ReviewedBy?: InputMaybe<string>;
  ReviewDate?: InputMaybe<string>;
  NextReviewDate?: InputMaybe<string>;
  CustomAttributeData?: InputMaybe<CustomAttributeData>;
}

type InsertDocumentVersionTypeInput =
  | { Type: 'file'; FileId: string }
  | { Type: 'html'; Content: string }
  | { Type: 'link'; Link: string };

export type InsertDocumentVersionInput = InsertDocumentVersionSharedInput &
  InsertDocumentVersionTypeInput;

type InsertDocumentVersionTRPCPayload = InsertDocumentVersionSharedInput &
  (
    | { Type: DocumentFileType.File; FileId: string }
    | { Type: DocumentFileType.Html; Content: string }
    | { Type: DocumentFileType.Link; Link: string }
  );

/**
 * Translates the loose, string-typed `Type` field used by the form layer into
 * the typed discriminated union the tRPC procedure expects.
 */
const toTRPCPayload = (
  variables: InsertDocumentVersionInput
): InsertDocumentVersionTRPCPayload => {
  const shared = {
    ParentDocumentId: variables.ParentDocumentId,
    Version: variables.Version,
    Summary: variables.Summary,
    ReasonForReview: variables.ReasonForReview,
    ReviewedBy: variables.ReviewedBy,
    ReviewDate: variables.ReviewDate,
    NextReviewDate: variables.NextReviewDate,
    CustomAttributeData: variables.CustomAttributeData,
  };
  switch (variables.Type) {
    case 'file':
      return {
        ...shared,
        Type: DocumentFileType.File,
        FileId: variables.FileId,
      };
    case 'html':
      return {
        ...shared,
        Type: DocumentFileType.Html,
        Content: variables.Content,
      };
    case 'link':
      return {
        ...shared,
        Type: DocumentFileType.Link,
        Link: variables.Link,
      };
  }
};

const toGraphQLVariables = (input: InsertDocumentVersionInput) => {
  const shared = {
    ParentDocumentId: input.ParentDocumentId,
    Version: input.Version,
    Summary: input.Summary,
    ReasonForReview: input.ReasonForReview,
    ReviewedBy: input.ReviewedBy,
    ReviewDate: input.ReviewDate,
    NextReviewDate: input.NextReviewDate,
    CustomAttributeData: input.CustomAttributeData,
  };
  switch (input.Type) {
    case 'file':
      return {
        ...shared,
        Type: Document_File_Type_Enum.File,
        FileId: input.FileId,
        Content: null,
        Link: null,
      };
    case 'html':
      return {
        ...shared,
        Type: Document_File_Type_Enum.Html,
        FileId: null,
        Content: input.Content,
        Link: null,
      };
    case 'link':
      return {
        ...shared,
        Type: Document_File_Type_Enum.Link,
        FileId: null,
        Content: null,
        Link: input.Link,
      };
  }
};

export const useInsertDocumentVersion = createMutationHook({
  name: 'insertDocumentVersion',
  mutationOptions: (trpc, onSuccess) =>
    trpc.frontend.documentFile.insert.mutationOptions({ onSuccess }),
  mapInput: toTRPCPayload,
  invalidate: (trpc) => [
    trpc.frontend.documentFile.documentFilesByDocumentId.queryKey(),
    trpc.frontend.documentFile.latestDocumentFile.queryKey(),
    trpc.frontend.documentFile.documentFileById.queryKey(),
    trpc.frontend.documentFile.publicDocumentFiles.queryKey(),
    trpc.frontend.documentFile.latestPublicDocumentFileByDocumentId.queryKey(),
    // COI-471 parity: Apollo twin also evicts `document` (a new version
    // changes the parent document's current-version surfaces).
    trpc.frontend.document.pathKey(),
  ],
  mapResponse: (result: { Id: string }): InsertDocumentVersionMutation => ({
    insertDocumentVersion: {
      __typename: 'IdOutput',
      Id: result.Id,
    },
  }),
  graphqlDocument: InsertDocumentVersionDocument,
  graphqlVariables: (input: InsertDocumentVersionInput) => ({
    ...toGraphQLVariables(input),
  }),
  evictFields: ['document'],
  refetchQueries: [namedOperations.Query.getDocumentFileById],
});
